/* eslint-disable @typescript-eslint/no-invalid-void-type */
/**
 * LinkedIn accounts hooks (CRUD + reconnect, optimistic updates).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { deleteJson, getJson, patchJson, postJson } from './_http';

/* ─── LinkedIn accounts — Wave 5b.1 (FULLY IMPLEMENTED) ────────────────── */

export interface LinkedinAccountRow {
  id: string;
  userId: string;
  name: string;
  initials: string;
  color: string;
  vanitySlug: string | null;
  linkedinUrn: string | null;
  profileUrl: string | null;
  avatarUrl: string | null;
  voiceLabel: string | null;
  linkedinPlan: 'free' | 'premium' | 'sales-navigator' | 'recruiter';
  salesNav: boolean;
  timezone: string;
  status: 'active' | 'paused' | 'error' | 'limited';
  statusInfo: string | null;
  statusChangedAt: string | number | null;
  cookieExpiresAt: string | number | null;
  lastHealthCheckAt: string | number | null;
  historyEligible: boolean | null;
  historyConvs: number | null;
  historySpan: string | null;
  aiMode: 'auto' | 'review' | 'manual';
  showOffTarget: boolean | null;
  offTargetMode: 'block' | 'suggest' | 'manual';
  autoLeadDetect: boolean | null;
  createdAt: string | number;
  updatedAt: string | number;
  archivedAt: string | number | null;
}

export interface LinkedinAccountsResponse {
  accounts: LinkedinAccountRow[];
}

export interface CreateLinkedinAccountParams {
  name: string;
  vanitySlug?: string;
  linkedinUrn?: string;
  profileUrl?: string;
  avatarUrl?: string;
  voiceLabel?: string;
  /** Cleartext li_at — encrypted server-side. */
  liAt?: string;
  source?: 'extension' | 'cookie';
}

export interface UpdateLinkedinAccountParams {
  id: string;
  name?: string;
  color?: string;
  voiceLabel?: string;
  status?: 'active' | 'paused' | 'error' | 'limited';
  statusInfo?: string;
  settings?: Partial<{
    aiMode: 'auto' | 'review' | 'manual';
    showOffTarget: boolean;
    offTargetMode: 'block' | 'suggest' | 'manual';
    autoLeadDetect: boolean;
  }>;
}

export interface ValidateCookieParams {
  liAt: string;
}

export interface ValidateCookieResponse {
  ok: true;
  profile: {
    name: string;
    headline: string;
    vanitySlug: string | null;
    avatarUrl: string | null;
    linkedinUrn: string | null;
  };
}

/** `GET /api/v1/linkedin-accounts` — list non-archived accounts. */
export function useLinkedinAccounts(
  options: { enabled?: boolean } = {},
): UseQueryResult<LinkedinAccountsResponse, ApiError> {
  const client = useApiClient();
  return useQuery<LinkedinAccountsResponse, ApiError>({
    queryKey: ['linkedin-accounts'],
    queryFn: () => getJson<LinkedinAccountsResponse>(client, '/api/v1/linkedin-accounts'),
    enabled: options.enabled,
    retry: false,
  });
}

/** `POST /api/v1/linkedin-accounts/validate-cookie`. */
export function useValidateCookie(): UseMutationResult<
  ValidateCookieResponse,
  ApiError,
  ValidateCookieParams
> {
  const client = useApiClient();
  return useMutation<ValidateCookieResponse, ApiError, ValidateCookieParams>({
    mutationFn: (vars) =>
      postJson<ValidateCookieResponse>(client, '/api/v1/linkedin-accounts/validate-cookie', vars),
  });
}

/** `POST /api/v1/linkedin-accounts` — create account from extension/cookie. */
export function useCreateLinkedinAccount(): UseMutationResult<
  { account: LinkedinAccountRow },
  ApiError,
  CreateLinkedinAccountParams
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ account: LinkedinAccountRow }, ApiError, CreateLinkedinAccountParams>({
    mutationFn: (vars) =>
      postJson<{ account: LinkedinAccountRow }>(client, '/api/v1/linkedin-accounts', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
    },
  });
}

/**
 * `PATCH /api/v1/linkedin-accounts/:id` — handles pause/resume/reconnect via
 * the `status` field plus voiceLabel/name/color edits and per-account
 * settings (aiMode, off-target, auto-lead-detect).
 *
 * Optimistic update: rewrites the cached row for `['linkedin-accounts']`
 * before the network round-trip; rolls back on error.
 */
export function useUpdateLinkedinAccount(): UseMutationResult<
  { account: LinkedinAccountRow },
  ApiError,
  UpdateLinkedinAccountParams,
  { previous: LinkedinAccountsResponse | undefined }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { account: LinkedinAccountRow },
    ApiError,
    UpdateLinkedinAccountParams,
    { previous: LinkedinAccountsResponse | undefined }
  >({
    mutationFn: ({ id, ...body }) =>
      patchJson<{ account: LinkedinAccountRow }>(
        client,
        `/api/v1/linkedin-accounts/${encodeURIComponent(id)}`,
        body,
      ),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['linkedin-accounts'] });
      const previous = qc.getQueryData<LinkedinAccountsResponse>(['linkedin-accounts']);
      if (previous) {
        qc.setQueryData<LinkedinAccountsResponse>(['linkedin-accounts'], {
          accounts: previous.accounts.map((acc) =>
            acc.id === vars.id
              ? {
                  ...acc,
                  ...(vars.name !== undefined ? { name: vars.name } : {}),
                  ...(vars.color !== undefined ? { color: vars.color } : {}),
                  ...(vars.voiceLabel !== undefined ? { voiceLabel: vars.voiceLabel } : {}),
                  ...(vars.status !== undefined ? { status: vars.status } : {}),
                  ...(vars.statusInfo !== undefined ? { statusInfo: vars.statusInfo } : {}),
                  ...(vars.settings?.aiMode !== undefined ? { aiMode: vars.settings.aiMode } : {}),
                  ...(vars.settings?.showOffTarget !== undefined
                    ? { showOffTarget: vars.settings.showOffTarget }
                    : {}),
                  ...(vars.settings?.offTargetMode !== undefined
                    ? { offTargetMode: vars.settings.offTargetMode }
                    : {}),
                  ...(vars.settings?.autoLeadDetect !== undefined
                    ? { autoLeadDetect: vars.settings.autoLeadDetect }
                    : {}),
                }
              : acc,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<LinkedinAccountsResponse>(['linkedin-accounts'], ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
    },
  });
}

/** `DELETE /api/v1/linkedin-accounts/:id` — soft delete (Disconnect, US-174). */
export function useDeleteLinkedinAccount(): UseMutationResult<
  { ok: true; alreadyArchived?: boolean },
  ApiError,
  { id: string },
  { previous: LinkedinAccountsResponse | undefined }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { ok: true; alreadyArchived?: boolean },
    ApiError,
    { id: string },
    { previous: LinkedinAccountsResponse | undefined }
  >({
    mutationFn: ({ id }) =>
      deleteJson<{ ok: true; alreadyArchived?: boolean }>(
        client,
        `/api/v1/linkedin-accounts/${encodeURIComponent(id)}`,
      ),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: ['linkedin-accounts'] });
      const previous = qc.getQueryData<LinkedinAccountsResponse>(['linkedin-accounts']);
      if (previous) {
        qc.setQueryData<LinkedinAccountsResponse>(['linkedin-accounts'], {
          accounts: previous.accounts.filter((acc) => acc.id !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<LinkedinAccountsResponse>(['linkedin-accounts'], ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
    },
  });
}

/**
 * `POST /api/v1/linkedin-accounts/:id/reconnect` — record the user's
 * reconnect intent (US-171). The server emits an audit-log entry and
 * returns 204; the actual recovery is driven by the extension SW (open
 * managed LinkedIn tab + heartbeat re-validation), so this mutation does
 * NOT mutate the row in the cache. The UI uses a local "reconnecting" flag
 * to render the transient pill; once the heartbeat flips the server-side
 * status off `error`, query invalidation reconciles automatically.
 */
export function useReconnectLinkedinAccount(): UseMutationResult<void, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      postJson<void>(client, `/api/v1/linkedin-accounts/${encodeURIComponent(id)}/reconnect`, {}),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['linkedin-accounts'] });
    },
  });
}

/** Backwards-compatible aliases — kept so older imports keep typechecking. */
export const usePauseLinkedinAccount = useUpdateLinkedinAccount;
export const useResumeLinkedinAccount = useUpdateLinkedinAccount;
export const useDisconnectLinkedinAccount = useDeleteLinkedinAccount;
